import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc'

const buttonVariants = cva(
	'ring-ring ring-offset-background inline-flex items-center justify-center rounded-lg text-sm font-medium ring-offset-2 outline-hidden transition-colors focus-within:ring-2 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/80',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				// outline is an alias of secondary (DESIGN.md §9).
				outline:
					'border-border-strong text-foreground hover:bg-accent border bg-transparent',
				secondary:
					'border-border-strong text-foreground hover:bg-accent border bg-transparent',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-10 px-4 py-2 max-md:h-11',
				wide: 'px-24 py-5',
				sm: 'h-9 rounded-lg px-3',
				lg: 'h-11 rounded-lg px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'size-10 max-md:size-11',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

export type ButtonProps = React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: ButtonProps) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
